import { LoaderCircle, RefreshCw, TriangleAlert } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";

export type StaleSearchIndexScope = "visible" | "workspace";

interface StaleSearchIndexNoticeProps {
  /** Documents on this surface whose search index predates the current pipeline version. */
  staleCount: number;
  /** How many of those the reader may rerun right now (not already running). */
  reindexableCount: number;
  /** How many of the stale documents have a rerun in flight. */
  runningCount?: number;
  /** How many one click starts; the rest wait for the next click. */
  batchSize: number;
  /** "visible": the hits on screen. "workspace": every document of the open workspace. */
  scope: StaleSearchIndexScope;
  /** One line instead of title and body, for tight layouts such as the global search overview. */
  compact?: boolean;
  onReindexAll?: () => void;
  className?: string;
}

const documents = (count: number) =>
  count === 1 ? "1 dokument" : `${count} dokumenter`;

const EXPLANATION =
  "Efter en platformopgradering skal dokumenterne klargøres igen, før søgningen finder dem og antallet af matches er retvisende. Dokumenterne kan stadig åbnes og bruges.";

/**
 * The plain-language companion to the amber marker (LAR-235): shown above any list that
 * holds documents with an outdated search index — the Workspaces page content search,
 * the files tab and global search. It says what the marker means, that nothing is
 * broken, and offers the fix as one click, so a page of amber icons never has to be
 * decoded from tooltips.
 */
export const StaleSearchIndexNotice = ({
  staleCount,
  reindexableCount,
  runningCount = 0,
  batchSize,
  scope,
  compact = false,
  onReindexAll,
  className,
}: StaleSearchIndexNoticeProps) => {
  if (staleCount <= 0) return null;

  const title =
    scope === "workspace"
      ? `Søgeindekset er forældet for ${documents(staleCount)} i dette workspace`
      : `Søgeindekset er forældet for ${staleCount} af de viste dokumenter`;
  const startsNow = Math.min(reindexableCount, batchSize);
  const canRerun = startsNow > 0 && onReindexAll !== undefined;
  const actionLabel = `Genkør kildeklargøring for ${documents(startsNow)}${
    reindexableCount > batchSize ? " ad gangen" : ""
  }`;

  const action = canRerun ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 shrink-0 gap-1 px-2 text-xs"
      onClick={onReindexAll}
    >
      <RefreshCw className="size-3.5" aria-hidden="true" />
      {actionLabel}
    </Button>
  ) : null;

  const running =
    runningCount > 0 ? (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <LoaderCircle className="size-3.5 animate-spin" aria-hidden="true" />
        Kildeklargøring kører for {documents(runningCount)}.
      </span>
    ) : null;

  const askOwner =
    !canRerun && runningCount === 0
      ? "Bed ejeren af workspacet om at genkøre kildeklargøring."
      : null;

  return (
    <Alert
      role="status"
      className={cn(
        "border-warning/50 bg-warning/10 text-foreground [&>svg]:text-warning",
        compact && "items-center py-2",
        className
      )}
    >
      <TriangleAlert aria-hidden="true" />
      {compact ? (
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-sm font-medium">{title}</span>
          {running}
          {askOwner && (
            <span className="text-xs text-muted-foreground">{askOwner}</span>
          )}
          <span className="grow" />
          {action}
        </div>
      ) : (
        <>
          <AlertTitle>{title}</AlertTitle>
          <AlertDescription className="text-foreground/80">
            {EXPLANATION}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {action}
              {running}
              {askOwner && <span className="text-xs">{askOwner}</span>}
            </div>
          </AlertDescription>
        </>
      )}
    </Alert>
  );
};

export default StaleSearchIndexNotice;
